/** The journal's pure parts: the day's completeness and the timeline search (U061, U062). */
import type { JournalEntry, JournalRow, JournalSummary } from "../../contracts/ops.ts";

export interface JournalModel {
  readonly summary: JournalSummary | null;
  /** Entries in receipt order. */
  readonly entries: readonly JournalEntry[];
}

/** Fold the rows into the day's summary and its entries in receipt order. */
export function reduceJournal(rows: readonly JournalRow[]): JournalModel {
  return {
    summary: rows.find((row): row is JournalSummary => row.kind === "journal_summary") ?? null,
    entries: rows
      .filter((row): row is JournalEntry => row.kind === "journal_entry")
      .sort((a, b) => a.receiptSeq - b.receiptSeq),
  };
}

/** The entries whose listing, decision, order, command, event or entry id contains `query`. */
export function searchEntries(
  entries: readonly JournalEntry[],
  query: string,
): readonly JournalEntry[] {
  const wanted = query.trim().toLowerCase();
  if (wanted === "") {
    return entries;
  }
  return entries.filter((entry) =>
    [
      entry.entryId,
      entry.listing,
      entry.decisionId,
      entry.orderId,
      entry.commandId,
      entry.eventId,
    ].some((id) => id?.toLowerCase().includes(wanted) === true),
  );
}

/** How the day's journal states its own completeness. */
export function completeness(summary: JournalSummary | null): {
  readonly complete: boolean;
  readonly text: string;
} {
  if (summary === null) {
    return {
      complete: false,
      text: "No journal summary was reported, so completeness is unknown.",
    };
  }
  if (!summary.complete) {
    return {
      complete: false,
      text: `Incomplete: ${String(summary.missingReceipts)} receipt(s) missing in the range; do not read this day as the whole story.`,
    };
  }
  return {
    complete: true,
    text:
      summary.firstReceiptSeq === null
        ? "Complete: no entries were received for this day."
        : `Complete: receipts ${String(summary.firstReceiptSeq)} to ${String(summary.lastReceiptSeq)} with none missing.`,
  };
}
